import { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronLeft } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

export type CascadingOption = {
  value: string;
  label: string;
};

export type CascadingGroup = {
  value: string;
  label: string;
  children?: readonly CascadingOption[];
};

function groupForValue(groups: readonly CascadingGroup[], value: string): CascadingGroup | undefined {
  return groups.find((group) => {
    if (group.children?.some((child) => child.value === value)) return true;
    return group.value === value;
  });
}

/**
 * Two-level compact picker (country → state). Same chrome as {@link TimeRangeFilter}.
 */
export function CascadingFilter({
  label,
  detail,
  value,
  groups,
  onChange,
  ariaLabel,
  className,
  size = 'compact',
  title,
}: {
  label: string;
  /** Closed-state subtitle under the label. Omit or leave empty to hide. */
  detail?: string;
  value: string;
  groups: readonly CascadingGroup[];
  onChange: (value: string) => void;
  ariaLabel: string;
  className?: string;
  size?: 'default' | 'compact';
  title?: string;
}) {
  const [open, setOpen] = useState(false);
  const [activeGroupValue, setActiveGroupValue] = useState<string | null>(null);
  const compact = size === 'compact';

  const activeGroup = useMemo(
    () => groups.find((group) => group.value === activeGroupValue),
    [activeGroupValue, groups],
  );

  useEffect(() => {
    if (!open) {
      setActiveGroupValue(null);
    }
  }, [open]);

  const rows = activeGroup?.children ?? groups;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={ariaLabel}
          aria-expanded={open}
          {...(title ? { title } : {})}
          className={cn(
            'flex w-[280px] items-center justify-between border bg-[#101010] px-3 text-left outline-none transition-colors',
            compact ? 'h-[49px]' : 'h-[62px]',
            open
              ? 'rounded-t-lg border border-b-0 border-[#0194F5]'
              : 'rounded-lg border border-[#272729] hover:border-[#504F4E]',
            className,
          )}
        >
          <span className={cn('min-w-0', !detail?.trim() && 'flex flex-col justify-center')}>
            <span
              className={cn(
                'block truncate font-normal text-white',
                compact ? 'text-sm leading-5' : 'text-base leading-6',
              )}
            >
              {label}
            </span>
            {detail?.trim() ? (
              <span className="block truncate text-xs font-light leading-[14px] text-[#D1D5DB]">
                {detail}
              </span>
            ) : null}
          </span>
          <ChevronDown
            className={cn(
              'ml-2 shrink-0 text-white transition-transform',
              compact ? 'size-4' : 'size-5',
              open && 'rotate-180',
            )}
            aria-hidden
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={0}
        className="w-[var(--radix-popover-trigger-width)] rounded-t-none rounded-b-lg border border-t-0 border-[#0194F5] bg-[#101010] p-2 shadow-none data-[state=open]:animate-none"
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        {activeGroup ? (
          <button
            type="button"
            className="mb-1 flex h-9 w-full items-center gap-1 rounded px-2 text-left text-xs text-[#D1D5DB] hover:bg-[#272729] hover:text-white"
            onClick={() => setActiveGroupValue(null)}
          >
            <ChevronLeft className="size-4" aria-hidden />
            {activeGroup.label}
          </button>
        ) : null}
        <div className="flex max-h-72 flex-col gap-0.5 overflow-y-auto">
          {rows.map((row) => {
            const group = 'children' in row ? (row as CascadingGroup) : undefined;
            const hasChildren = Boolean(group?.children?.length);
            const selected = hasChildren
              ? groupForValue(groups, value)?.value === row.value
              : row.value === value;
            return (
              <button
                key={row.value}
                type="button"
                className={cn(
                  'h-11 w-full rounded px-3 text-left text-base font-normal leading-6 transition-colors',
                  selected
                    ? 'bg-[#0194F5] text-white'
                    : 'text-[#D1D5DB] hover:bg-[#272729] hover:text-white',
                )}
                onClick={() => {
                  if (hasChildren) {
                    setActiveGroupValue(row.value);
                    return;
                  }
                  onChange(row.value);
                  setOpen(false);
                }}
              >
                {row.label}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
